import { startCase } from 'lodash'
import PropTypes from 'prop-types'

import GlyphIcon from 'components/common/GlyphIcon'
import Icon from 'components/Icon'
import {
  isRunHalting,
  getScenarioStepCount,
  getCurrentStepForRun,
  isRunInCompletedStage,
  getRunStage
} from 'components/scenarios/utils'
import useAgent from 'hooks/use-agent'
import useAuth from 'hooks/use-auth'
import theme from 'theme'
import { naturalSortBy } from 'utils/list'

import AttackRow, {
  AttackDescription,
  Status,
  HaltButton,
  DetailsChevron
} from './common/AttackRow'

export default function ScenarioRow({
  scenario,
  doFetch,
  clickableRow,
  selectableRow,
  onRowSelected,
  onHaltAttack,
  showHaltButton,
  onClick,
  extraRowContent,
  isScenarioRun,
  checked
}) {
  const { currentTeamId } = useAuth()
  const { name, guid, scenario_id } = scenario
  const scenarioId = guid ?? scenario_id
  const { data: runs } = useAgent(
    `/scenarios/${scenarioId}/runs?teamId=${currentTeamId}`,
    [],
    {
      shouldCache: true,
      interval: 10000,
      skipRequest: isScenarioRun
    }
  )

  const sortedRuns = naturalSortBy(runs, (r) => r.created_at, 'desc')

  const lastRun = isScenarioRun ? scenario : sortedRuns[0] ?? {}
  const totalSteps = isScenarioRun ? null : getScenarioStepCount(scenario)
  const currentStep = getCurrentStepForRun(lastRun)
  const runNumber = lastRun?.run_number
  const isCompleted = isRunInCompletedStage(lastRun)
  const isHalting = isRunHalting(lastRun) && !isCompleted

  // this smells like an opportunity to redo how this component works
  let extraColumns = ''
  if (showHaltButton) {
    extraColumns = '64px 24px'
  } else if (extraRowContent) {
    extraColumns = '128px'
  } else {
    extraColumns = '24px'
  }

  const runStage = getRunStage(scenario)

  const scenarioUrl = runNumber
    ? `/scenarios/detail/${scenarioId}/runs/${runNumber}`
    : `/scenarios/detail/${scenarioId}`

  return (
    <AttackRow
      {...(isCompleted
        ? { 'data-completed-scenario-row': true }
        : { 'data-active-scenario-row': true })}
      checked={checked}
      clickableRow={clickableRow}
      css={{
        gridTemplateColumns: `${
          selectableRow ? `${theme.space[4]}px` : ''
        } 1fr ${extraColumns}`
      }}
      selectableRow={selectableRow}
      to={clickableRow && scenarioUrl}
      onClick={onClick}
      onRowSelected={() => onRowSelected({ runNumber })}
    >
      <AttackDescription
        description={
          isHalting ? (
            'Halting...'
          ) : isCompleted ? (
            <span>{runStage ? startCase(runStage) : ''}</span>
          ) : (
            <span>
              Scenario:{' '}
              <Status as="span">
                Step {currentStep} of {totalSteps} is running...
              </Status>
            </span>
          )
        }
        icon={
          <GlyphIcon>
            <Icon
              css={{
                fontSize: theme.fontSizes[4],
                color: theme.colors.gray[1]
              }}
              icon="clipboardList"
            />
          </GlyphIcon>
        }
        name={name}
      />
      {showHaltButton && (
        <HaltButton
          fetchAttack={doFetch}
          isHalting={isHalting}
          runInfo={lastRun}
          title="Halt scenario"
          trackedEvent="SingleScenarioHalted"
          trackingOptions={{ scenarioId, runNumber: runNumber }}
          type="scenario"
          onConfirmRequest={({ reason } = {}) =>
            onHaltAttack({ reason, runNumber })
          }
        />
      )}
      {!showHaltButton && extraRowContent}
      {!extraRowContent && clickableRow && <DetailsChevron />}
    </AttackRow>
  )
}

ScenarioRow.propTypes = {
  scenario: PropTypes.object,
  doFetch: PropTypes.func,
  onRowSelected: PropTypes.func,
  onHaltAttack: PropTypes.func,
  clickableRow: PropTypes.bool,
  selectableRow: PropTypes.bool,
  showHaltButton: PropTypes.bool,
  rowLink: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  extraRowContent: PropTypes.node,
  isScenarioRun: PropTypes.bool,
  onClick: PropTypes.func,
  checked: PropTypes.bool
}
